import React, { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Play, Clock, Lock } from "lucide-react";
import { api, formatErr } from "@/lib/api";
import { resolveImg } from "@/lib/utils";
import { themeWord } from "@/components/site/MeditationBits";

function SecurePlayer({ token, slug }) {
  const ref = useRef(null);
  const [url, setUrl] = useState(null);
  const [err, setErr] = useState("");
  const resumeAt = useRef(0);
  const fetchUrl = async () => {
    try { const { data } = await api.post(`/public/access/${token}/play/${slug}`); setErr(""); setUrl(data.url); }
    catch (e) { setErr(formatErr(e)); }
  };
  useEffect(() => { fetchUrl(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  // Signed URLs are short-lived: on expiry fetch a fresh one and resume where the buyer was.
  const onError = () => { resumeAt.current = ref.current?.currentTime || 0; fetchUrl(); };
  const onLoaded = () => { if (resumeAt.current && ref.current) { ref.current.currentTime = resumeAt.current; ref.current.play(); resumeAt.current = 0; } };
  if (err) return <p className="mt-4 text-sm text-[#7A5C1F]" data-testid={`player-error-${slug}`}>{err}</p>;
  if (!url) return <div className="mt-4 aspect-video rounded-2xl bg-[#F3EFEA] animate-pulse" />;
  return (
    <video ref={ref} src={url} controls autoPlay playsInline controlsList="nodownload" disablePictureInPicture
      onContextMenu={e => e.preventDefault()} onError={onError} onLoadedMetadata={onLoaded}
      className="mt-4 w-full rounded-2xl bg-black" data-testid={`secure-player-${slug}`} />
  );
}

function AccessCard({ m, token }) {
  const [open, setOpen] = useState(false);
  const img = resolveImg(m.image_url);
  return (
    <article className="soft-card !p-5 sm:!p-7" data-testid={`access-card-${m.slug}`}>
      <div className="flex gap-4 items-center">
        <div className="relative w-20 h-20 sm:w-24 sm:h-24 rounded-2xl overflow-hidden bg-[#F3EFEA] shrink-0">
          {img ? <img src={img} alt="" className="absolute inset-0 w-full h-full object-cover" />
            : <span className="absolute inset-0 flex items-center justify-center font-serif text-lg text-[#E07A5F]/50">{themeWord(m.theme)}</span>}
        </div>
        <div className="min-w-0">
          <p className="text-[10px] uppercase tracking-[0.25em] text-terracotta">{m.theme}</p>
          <h2 className="font-serif text-2xl text-[#2D2422] leading-tight">{m.name}</h2>
          <p className="text-sm italic text-[#7E736E]">{m.subtitle}</p>
          <p className="text-xs text-[#7E736E] mt-1">{[m.duration, m.language].filter(Boolean).join(" · ")}</p>
        </div>
      </div>
      {m.available ? (open ? <SecurePlayer token={token} slug={m.slug} />
        : <button onClick={() => setOpen(true)} className="btn-primary mt-5 w-full sm:w-auto !py-3.5" data-testid={`access-play-${m.slug}`}><Play size={16} className="mr-2" />Play Meditation</button>)
        : <p className="mt-5 inline-flex items-center gap-2 text-sm text-[#7A5C1F] bg-[#FBF3E4] rounded-full px-4 py-2" data-testid={`access-preparing-${m.slug}`}><Clock size={15} />Your meditation is being prepared. Please check back soon.</p>}
    </article>
  );
}

export default function MeditationAccessPage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [bad, setBad] = useState(false);
  useEffect(() => {
    const meta = document.createElement("meta"); meta.name = "robots"; meta.content = "noindex, nofollow";
    document.head.appendChild(meta);
    api.get(`/public/access/${token}`).then(r => setData(r.data)).catch(() => setBad(true));
    return () => meta.remove();
  }, [token]);
  if (bad) return (
    <div className="container-ic py-24 max-w-xl" data-testid="access-invalid">
      <h1 className="font-serif text-4xl text-[#2D2422]">This access link isn't valid.</h1>
      <p className="mt-3 text-[#5C5955]">Please use the private link from your purchase confirmation, or contact us with your payment reference.</p>
      <Link to="/meditations" className="btn-ghost mt-7">Back to Meditations</Link>
    </div>
  );
  if (!data) return <div className="min-h-[60vh]" />;
  return (
    <div className="container-ic py-12 sm:py-16 max-w-3xl fade-up" data-testid="access-page">
      <p className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-terracotta"><Lock size={13} />Private access</p>
      <h1 className="font-serif text-4xl sm:text-5xl text-[#2D2422] mt-3">Your Meditations</h1>
      <p className="mt-3 text-[15px] text-[#5C5955]">Welcome, <span data-testid="access-buyer-name">{data.buyer_name}</span>. {data.product_kind === "collection" ? "Your Complete Collection includes all four meditations." : "Here is the meditation you purchased."} Bookmark this page to return any time.</p>
      <div className="mt-8 space-y-5">{data.items.map(m => <AccessCard key={m.slug} m={m} token={token} />)}</div>
    </div>
  );
}
